import {autobind} from "core-decorators";
import {createBrowserHistory, Location as HistoryLocation} from "history";
import {reaction, when} from "mobx";
import ReactGA from "react-ga4";

import Session from "./Session";

@autobind
export default class History {
  disposers: Array<() => any> = [];
  history = createBrowserHistory();
  session: Session;

  constructor(session: Session) {
    this.session = session;
    // Listen to history changes to push pageviews to Google Analytics
    // https://mtm.dev/google-analytics-react-router/
    if (process.env.REACT_APP_GOOGLE_ANALYTICS_ID) this.history.listen(this.trackPage);
    // Wait for session store to be ready (i.e. load user and ensemble data) to load Google Analytics
    when(() => this.session.initialized).then(this.setupGoogleAnalytics);
  }

  dispose() {
    this.disposers.forEach((disposer) => disposer());
    this.disposers = [];
  }

  async setupGoogleAnalytics() {
    if (!process.env.REACT_APP_GOOGLE_ANALYTICS_ID) return;
    // Set userId and ensembleId in google analytics tracker
    // Note: userId is an existing field but ensembleId is not. Any custom field has to be included as dimensionX/metricX.
    // See: https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#customs
    this.disposers.push(
      reaction(
        () => ({user: this.session.voicefulUser, ensemble: this.session.selectedEnsemble}),
        ({user, ensemble}) =>
          ReactGA.set({userId: user?.id, dimension1: user?.id.toString(), dimension2: ensemble?.id?.toString()}),
        {fireImmediately: true},
      ),
    );
    // Track the first render
    this.trackPage(window.location);
  }

  trackPage(location: Location | HistoryLocation) {
    const page = location.pathname + location.search;
    ReactGA.send({hitType: "pageview", page});
  }
}
